<script setup lang="ts">
  import { useHttpClient, useConfirmation, useRoute, useTranslator } from '@othercommerce/foundation';
  import type { OrderCouponDiscount } from '@storefront/resources';
  import { DiscountMode } from '@othercommerce/domain';

  interface Props {
    coupon: OrderCouponDiscount;
  }

  const props = defineProps<Props>();
  const route = useRoute();
  const http = useHttpClient();
  const confirm = useConfirmation();
  const translator = useTranslator();

  function onDestroy() {
    confirm({
      callback: () => http.delete(route('web.cart.coupon.destroy', { coupon: props.coupon.id })),
      title: translator.trans('SHOP.CART.HEADER.COUPON_REMOVE'),
      description: translator.trans('SHOP.CART.MESSAGE.COUPON_REMOVE'),
      yes: translator.trans('SHOP.ACTION.YES'),
      no: translator.trans('SHOP.ACTION.CANCEL'),
    }).catch(() => void (0));
  }
</script>
<template>
  <article class="order-coupon">
    <header class="order-coupon__header">
      <div v-if="coupon.name" class="order-coupon__data">
        <h3 class="order-coupon__heading">{{ $t('SHOP.CART.HEADER.COUPON_APPLIED') }}</h3>
        <p class="order-coupon__name">{{ coupon.name }}</p>
      </div>
      <div v-if="coupon.code" class="order-coupon__data">
        <h3 class="order-coupon__heading">{{ $t('SHOP.CART.HEADER.COUPON_CODE') }}</h3>
        <p class="order-coupon__code">{{ coupon.code }}</p>
      </div>
    </header>
    <section class="order-coupon__wrapper">
      <article class="order-coupon__content">
        <SfDiscount v-if="coupon.value > 0" :type="DiscountMode.FIXED" :value="coupon.value" class="order-coupon__discount" />
        <span v-if="coupon.isFreeShipping" class="order-coupon__free-shipping">{{ $t('SHOP.CART.LABEL.FREE_SHIPPING') }}</span>
      </article>
      <SfButton small transparent danger @click="onDestroy" class="order-coupon__delete">
        <template #before>
          <SfIconTrash aria-hidden="true" />
        </template>
        <template #default>
          {{ $t('SHOP.ACTION.DESTROY') }}
        </template>
      </SfButton>
    </section>
  </article>
</template>
<style lang="scss">
  :root {
    --sf-order-coupon-border-radius: var(--border-radius);
    --sf-order-coupon-border-width: 1px;
    --sf-order-coupon-border-style: solid;
    --sf-order-coupon-border-color: var(--secondary-color);
    --sf-order-coupon-shipping-color: var(--secondary-color);
    --sf-order-coupon-background: none;
  }

  .order-coupon {
    font-size: 0.875rem;
    margin-top: 1rem;
    border-radius: var(--sf-order-coupon-border-radius);
    border-color: var(--sf-order-coupon-border-color);
    border-style: var(--sf-order-coupon-border-style);
    border-width: var(--sf-order-coupon-border-width);
    background: var(--sf-order-coupon-background);
    padding: 0.5rem 0.75rem;

    &__header {
      font-weight: var(--font-weight-semi);
      margin-bottom: 0.25rem;
    }

    &__data {
      margin-bottom: 0.25rem;
    }

    &__heading {
      font-weight: var(--font-weight-regular);
      color: var(--secondary-color);
    }

    &__code,
    &__name,
    &__heading {
      font-size: 0.875rem;
    }

    &__wrapper {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-top: 0.75rem;
    }

    &__content {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: 0.125rem;
    }

    &__free-shipping {
      font-size: 0.75rem;
      margin-top: 0.25rem;
      color: var(--secondary-color);
    }
  }
</style>
